// Identity (account) selection for the Request Access basket — PLO-4704.
//
// A person can own several accounts on the same integration (e.g. a standard
// SSO account and a separate elevated/admin one). When a resource lives on an
// integration where the requester has more than one account, they must be able
// to choose WHICH account the grant lands on — otherwise it silently defaults
// to one that may already have the access, or the wrong one entirely.
//
// This is orthogonal to duration/reason (which live in useBasket's per-item
// cfg): identity applies per RESOURCE regardless of the bulk/customise toggle,
// and today only for the SELF recipient — we only hold the current user's
// `activeIdentities`. Subordinate/group recipients fall back to the backend
// default (no choice surfaced).

import { useMemo, useState } from "react";
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import type { MFMember } from "state/members/member";
import {
  isAppItem,
  NO_ELIGIBLE_ACCOUNT,
  optionsForItem,
  resolveSelection,
  type IdentityOption,
} from "./identityOptions";
import {
  recipientRoles,
  selectedRoleIds,
  type RecipientRoleSelection,
} from "./roleSelection";
import { resourceKey, type FlatRequestItem } from "./types";

export type { IdentityOption } from "./identityOptions";

export interface IdentitySelection {
  /**
   * The recipient the account choice applies to. Only the self recipient gets
   * a picker (we only have the current user's identities); everyone else uses
   * the backend default.
   */
  selfRecipientId?: string;
  /** Eligible accounts for a basket item; >1 ⇒ the inline picker is shown. */
  getOptions: (itemId: string) => IdentityOption[];
  /**
   * Selected account ids for an item. A resource can be requested for MORE than
   * one of the requester's accounts at once (each becomes its own request), so
   * this is a list, not a single value. A multi-account row defaults to one
   * account; empty means the user cleared it (invalid — excluded from submit).
   */
  getSelected: (itemId: string) => string[];
  /** Add/remove an account from an item's selection (may drop to empty). */
  toggle: (itemId: string, identityId: string) => void;
  /** itemId → chosen identityIds, consumed by the submit payload builder. */
  selection: Record<string, string[]>;
  /** Why the row can't be sent for the requester on any account, if so. */
  problem: (itemId: string) => string | null;
  /** Still asking which accounts the policies admit. */
  loading: boolean;
}

export const useIdentitySelection = (
  me: MFMember | undefined,
  items: FlatRequestItem[],
  roleSelection: RecipientRoleSelection,
  requestsForSelf: boolean,
): IdentitySelection => {
  const queries = useMemo(() => {
    if (!me || !requestsForSelf) return [];
    const roles = recipientRoles(roleSelection, me.id);
    return items
      .filter((item) => !isAppItem(item))
      .map((item) => ({
        itemId: item.id,
        catalogItemId: item.catalogItemId,
        selectableResourceId: resourceKey(item),
        memberId: me.id,
        entitlementIds: [...selectedRoleIds(item, roles)].sort(),
      }));
  }, [items, me, roleSelection, requestsForSelf]);
  const { eligible, loading } = MF_ACCESS.useEligibleIdentities(queries);

  const optionsByItem = useMemo(() => {
    const map: Record<string, IdentityOption[]> = {};
    for (const item of items)
      map[item.id] = optionsForItem(item, me, eligible[item.id]);
    return map;
  }, [items, me, eligible]);

  // Explicit user choices only; a row absent here means "use the default". By
  // deriving the effective selection from these (rather than seeding state)
  // there's no effect and no flash, and a cleared row (stored as `[]`) is
  // naturally kept distinct from an untouched one — nothing handles `undefined`.
  const [overrides, setOverrides] = useState<Record<string, string[]>>({});

  // The effective selection: every multi-account row resolves to at least its
  // default single account unless the user overrode it (including clearing to
  // `[]`).
  const selection = useMemo<Record<string, string[]>>(() => {
    const out: Record<string, string[]> = {};
    for (const [itemId, opts] of Object.entries(optionsByItem)) {
      // A lone NHI must be sent explicitly (the backend default only resolves
      // human identities); a lone human identity / app resource relies on it.
      if (opts.length <= 1 && !opts[0]?.isNhi) continue;
      out[itemId] = resolveSelection(opts, overrides[itemId]);
    }
    return out;
  }, [optionsByItem, overrides]);

  return {
    selfRecipientId: me?.id,
    getOptions: (itemId) => optionsByItem[itemId] ?? [],
    getSelected: (itemId) => selection[itemId] ?? [],
    // Toggle membership. A row may drop to zero accounts so the user can swap by
    // removing then adding (either order); an empty multi-account row is flagged
    // invalid by the basket and excluded from submit until an account is chosen.
    toggle: (itemId, identityId) =>
      setOverrides((prev) => {
        const base = resolveSelection(
          optionsByItem[itemId] ?? [],
          prev[itemId],
        );
        const next = base.includes(identityId)
          ? base.filter((id) => id !== identityId)
          : [...base, identityId];
        return { ...prev, [itemId]: next };
      }),
    selection,
    problem: (itemId) =>
      eligible[itemId] && (optionsByItem[itemId] ?? []).length === 0
        ? NO_ELIGIBLE_ACCOUNT
        : null,
    loading,
  };
};
